import { HttpErrorResponse } from '@angular/common/http';
import { CloudError } from './cloud-error';
import { StackFrame } from 'stacktrace-js';
import { ZodError } from 'zod';

interface InnerError {
  code: string;
  message: string;
}

export function normalizeError(err: unknown): Error {
  if (err instanceof HttpErrorResponse) {
    const innerError = err?.error?.errors;
    if (isInnerErrorArray(innerError) && innerError.length > 0) {
      const newError = new CloudError(innerError[0].message, innerError[0].code);
      newError.stack = `HttpErrorResponse error: [${err.status}] ${err.message}\n url: ${err.url}}\n innerError: ${innerError[0].message}`;
      return newError;
    }
    const newError = new Error(err.message);
    newError.stack = `HttpErrorResponse error: [${err.status}] ${err.message}\n url: ${err.url}}`;
    return newError;
  }
  if (err instanceof ZodError) {
    const zodIssues = err.issues
      .map((issue) => {
        const path = issue.path.length > 0 ? issue.path.join('.') : 'root';
        return `Path: ${path}, Message: ${issue.message}`;
      })
      .join('\n');
    return new Error(zodIssues);
  }
  if (err instanceof Error) {
    return err;
  }
  return new Error(typeof err === 'string' ? err : JSON.stringify(err, null, 2) || 'Unknown error');
}

export async function captureStackTrace(error: Error): Promise<[string | undefined, StackFrame[]]> {
  try {
    const rawStackFrames = await StackTrace.fromError(error);
    const limitedStack = rawStackFrames.slice(0, 10);
    const stackTrace = limitedStack.map((frame) => frame.toString()).join('\n');
    return [stackTrace, rawStackFrames];
  } catch (stackError) {
    return [undefined, []];
  }
}

function isInnerErrorArray(value: unknown): value is InnerError[] {
  return (
    Array.isArray(value) &&
    value.every(
      (item) =>
        typeof item === 'object' &&
        'code' in item &&
        typeof item.code === 'string' &&
        'message' in item &&
        typeof item.message === 'string',
    )
  );
}
